import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Guid } from 'guid-typescript';

import { ExpenditureItem, ExpenditureReport, Project } from '../../../../../../common/models/project';
import { FileInfo } from '../../../../../../common/models/file';
import { ProjectService } from '../../../../../../common/services/project.service';
import { FileService } from '../../../../../../common/services/file.service';
import { CostType } from '../../../../../../common/models/common';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';


@Component({
  standalone: true,
  selector: 'app-expenditure-view',
  templateUrl: './expenditure-view.component.html',
  styleUrls: ['./expenditure-view.component.scss'],
  imports: [
    CommonModule,
    MatStepperModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule,
  ],
})
export class ExpenditureViewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  isHandset: boolean = false;

  expenditureReport: ExpenditureReport | null = null;

  expenditureItemFileMap: { [itemId: string]: FileInfo[] } = {};
  reportInfoFields: Array<{ label: string; value: any }> = [];

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private fileService: FileService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
    .subscribe(result => {
      this.isHandset = result.matches;
    });

    const projectIdParam = this.route.snapshot.paramMap.get('projectId');
    const reportIdParam = this.route.snapshot.paramMap.get('reportId');

    if (!projectIdParam || !reportIdParam) {
      console.error('Missing projectId or reportId in route.');
      return;
    }

    const projectId = Guid.parse(projectIdParam);
    const reportId = Guid.parse(reportIdParam);

   this.projectService.getExpenditureReport(projectId, reportId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (foundReport) => {
        if (!foundReport) {
          console.error('ExpenditureReport not found.');
          return;
        }
        this.expenditureReport = foundReport;
        this.buildReportInfoFields();
        this.loadExpenditureItemAttachments();
      },
      error: (err) => console.error('Error loading expenditure report', err),
    });
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }

  private buildReportInfoFields(): void {
    if (!this.expenditureReport) return;

    this.reportInfoFields = [
      {
        label: 'Razdoblje izvještavanja - početak',
        value: this.expenditureReport.reportingPeriodStart,
      },
      {
        label: 'Razdoblje izvještavanja - kraj',
        value: this.expenditureReport.reportingPeriodEnd,
      },
    ];
  }


  private loadExpenditureItemAttachments(): void {
    if (!this.expenditureReport?.expenditureItems?.length) {
      return;
    }

    for (const item of this.expenditureReport.expenditureItems) {
      if (!item.fileStorageLocation) {
        continue;
      }
      this.fileService.listDocuments(item.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
        next: (files) => {
          this.expenditureItemFileMap[item.id.toString()] = files;
        },
        error: (err) => {
          console.error(`Error loading attachments for item ${item.id}`, err);
        },
      });
    }
  }


  downloadFile(file: FileInfo): void {
    this.fileService.downloadDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => console.error('Error downloading file', err),
    });
  }

  get directExpenditures(): ExpenditureItem[] {
    return this.expenditureReport?.expenditureItems.filter(
      i => i.type === CostType.Direct
    ) || [];
  }

  get indirectExpenditures(): ExpenditureItem[] {
    return this.expenditureReport?.expenditureItems.filter(
      i => i.type === CostType.Indirect
    ) || [];
  }

  get totalDirectContractedFunds(): number {
    return this.directExpenditures.reduce((sum, i) => sum + (i.contractedFundsAmount || 0), 0);
  }

  get totalDirectRealizedCosts(): number {
    return this.directExpenditures.reduce((sum, i) => sum + (i.realizedEligibleCosts || 0), 0);
  }

  get totalIndirectContractedFunds(): number {
    return this.indirectExpenditures.reduce((sum, i) => sum + (i.contractedFundsAmount || 0), 0);
  }

  get totalIndirectRealizedCosts(): number {
    return this.indirectExpenditures.reduce((sum, i) => sum + (i.realizedEligibleCosts || 0), 0);
  }

  get totalAllContracted(): number {
    return this.totalDirectContractedFunds + this.totalIndirectContractedFunds;
  }

  get totalAllRealized(): number {
    return this.totalDirectRealizedCosts + this.totalIndirectRealizedCosts;
  }
}